import { NavigationFailureType, isNavigationFailure } from "vue-router";

import { consoleSentry } from "@/consoleSentry";
import { CancelError } from "@/error";

const initPromise = Promise.withResolvers();

export function initConsoleSentry() {
  initPromise.resolve(consoleSentry);
}

export function initRealSentry(config) {
  import("@sentry/vue")
    .then((sentry) => {
      sentry.init({
        app: config.app,
        dsn: config.dsn,
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        // Without compression Replay needs no worker, so the CSP doesn't have to allow blob: workers
        integrations: [sentry.replayIntegration({ useCompression: false })],
        environment: config.environment,
        normalizeDepth: 4,
        beforeSend(event, hint) {
          const error = hint && hint.originalException;
          return isCancel(error) ? null : event;
        },
      });
      initPromise.resolve(sentry);
    })
    .catch(() => initPromise.resolve(consoleSentry));
}

export function setUser(user) {
  initPromise.promise.then((sentry) => {
    sentry.setUser({ id: user.id });
    sentry.setTag("company", user.company);
  });
}

export function resetUser() {
  initPromise.promise.then((sentry) => {
    sentry.setUser(null);
    sentry.setTag("company", null);
  });
}

export function addBreadcrumb(category, additionalProperties = {}) {
  initPromise.promise.then((sentry) => {
    sentry.addBreadcrumb({ ...additionalProperties, category });
  });
}

export function captureExceptionWithContext(exception, context = {}) {
  initPromise.promise.then((sentry) => {
    sentry.withScope((scope) => {
      setContexts(scope, context);
      sentry.captureException(exception, eventHint({ ...context, ...exception?.contexts }));
    });
  });
}

export function captureMessageWithContext(message, context = {}) {
  initPromise.promise.then((sentry) => {
    sentry.withScope((scope) => {
      setContexts(scope, context);
      sentry.captureMessage(message);
    });
  });
}

function isCancel(error) {
  // vue in non-prod mode additionally throws errors in handlers in the form of 'error in handler: "<root exception>"'
  return (
    error instanceof CancelError ||
    (typeof error === "string" && error.includes(': "CancelError: ')) ||
    isNavigationFailure(error, runtimeFailure)
  );
}

/* eslint-disable no-bitwise */
const runtimeFailure =
  NavigationFailureType.aborted |
  NavigationFailureType.cancelled |
  NavigationFailureType.duplicated;
/* eslint-enable no-bitwise */

function eventHint(contexts) {
  contexts.browser = { online: navigator.onLine };
  const eventId = contexts.request?.correlationId || contexts.request?.headers?.["correlation-id"];
  return {
    event_id: eventId,
    captureContext: { contexts },
  };
}

function setContexts(scope, context) {
  Object.entries(context).forEach(([key, value]) => {
    scope.setContext(key, value);
  });
}
